import type { Submission } from '@conform-to/react';
import { json, useFormAction, useNavigation } from '@remix-run/react';

/**
 * Combine multiple header objects into one (uses append so headers are not overridden)
 */
export function combineHeaders(
  ...headers: Array<ResponseInit['headers'] | null | undefined>
) {
  const combined = new Headers();
  for (const header of headers) {
    if (!header) continue;
    for (const [key, value] of new Headers(header).entries()) {
      combined.append(key, value);
    }
  }
  return combined;
}
/**
 * Combine multiple response init objects into one (uses combineHeaders)
 */
export function combineResponseInits(
  ...responseInits: Array<ResponseInit | null | undefined>
) {
  let combined: ResponseInit = {};
  for (const responseInit of responseInits) {
    combined = {
      ...responseInit,
      headers: combineHeaders(combined.headers, responseInit?.headers),
    };
  }
  return combined;
}

/**
 * Returns true if the current navigation is submitting the current route's
 * form. Defaults to the current route's form action and method POST.
 *
 * Defaults state to 'non-idle'
 *
 * NOTE: the default formAction will include query params, but the
 * navigation.formAction will not, so don't use the default formAction if you
 * want to know if a form is submitting without specific query params.
 */
export function useIsPending({
  formAction,
  formMethod = 'POST',
  state = 'non-idle',
}: {
  formAction?: string;
  formMethod?: 'POST' | 'GET' | 'PUT' | 'PATCH' | 'DELETE';
  state?: 'submitting' | 'loading' | 'non-idle';
} = {}) {
  const contextualFormAction = useFormAction();
  const navigation = useNavigation();
  const isPendingState =
    state === 'non-idle'
      ? navigation.state !== 'idle'
      : navigation.state === state;
  return (
    isPendingState &&
    navigation.formAction === (formAction ?? contextualFormAction) &&
    navigation.formMethod === formMethod
  );
}

export function getErrorMessage(error: unknown) {
  if (typeof error === 'string') return error;
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message;
  }
  console.error('Unable to get error message for error', error);
  return 'Unknown Error';
}

export function handleSubmissionReplyOnFail(submission: Submission<unknown>) {
  if (submission.status !== 'success') {
    throw json(
      { result: submission.reply() },
      { status: submission.status === 'error' ? 400 : 200 }
    );
  }
}

export async function delay(time_ms: number) {
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      resolve();
    }, time_ms);
  });
}

export function removeDuplicates<T>(array: T[], key: keyof T): T[] {
  return array.filter(
    (item, index, self) => index === self.findIndex((t) => t[key] === item[key])
  );
}

export function Exists<T>(item: T | undefined): item is T {
  return !!item;
}

export function getMillisecondsBetweenTimes(time1: Date, time2: Date): number {
  const milliseconds1 = time1.getTime();
  const milliseconds2 = time2.getTime();

  return Math.abs(milliseconds2 - milliseconds1);
}

export function convertLadderRungScoreNames(scores: {
  functionalScore: number | null;
  comfortableScore: number | null;
  delightfulScore: number | null;
  meaningfulScore: number | null;
  usableScore: number | null;
}) {
  return {
    functional: scores.functionalScore,
    comfortable: scores.comfortableScore,
    delightful: scores.delightfulScore,
    meaningful: scores.meaningfulScore,
    usable: scores.usableScore,
  };
}
